import React, { useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import EAButton from "../../components/common/EAButton";
import i18next from "i18next";
import useStyles from "./style";

function ReportGenerationFlow({ availableReports, onSubmit, onCancel }) {
  const { classes } = useStyles();
  const [selectedReport, setSelectedReport] = useState(null);
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [step, setStep] = useState(1); // 1: select report, 2: select format

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    setStep(2);
  };

  const handleFormatChange = (event, newFormat) => {
    if (newFormat !== null) {
      setExportFormat(newFormat);
    }
  };

  const handleGenerate = () => {
    onSubmit(selectedReport.reportId, exportFormat, {});
  };

  const handleBack = () => {
    onCancel();
  };

  const handleStepClick = (stepNumber) => {
    if (stepNumber === 0 && step === 2) {
      setStep(1);
      setSelectedReport(null);
    }
  };

  const steps = [
    { label: "Sélection de l'export", icon: "description" },
    { label: "Format d'export", icon: "settings" }
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
        <Typography variant="h2" className={classes.generationFlowTitle} style={{ marginBottom: 0 }}>
          {i18next.t("GENERATE_REPORT")}
        </Typography>
        <Button
          startIcon={<Icon>close</Icon>}
          onClick={handleBack}
          sx={{
            color: "#9A9AAF",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          Fermer
        </Button>
      </div>

      <Card className={classes.generationFlowCard}>
        <div className={classes.formContainer}>
          {/* Header with step indicators */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
              <Icon sx={{ color: "primary.main", fontSize: "32px" }}>
                file_download
              </Icon>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                Générer un nouveau export
              </Typography>
            </div>

            <Stepper
              activeStep={step - 1}
              className={classes.stepper}
              sx={{
                marginBottom: "32px",
                "& .MuiStepLabel-root .Mui-completed": {
                  color: "primary.main",
                },
                "& .MuiStepLabel-root .Mui-active": {
                  color: "primary.main",
                },
                "& .MuiStepConnector-line": {
                  borderColor: step === 2 ? "primary.main" : "#e0e0e0",
                },
              }}
            >
              {steps.map((stepData, index) => (
                <Step key={stepData.label}>
                  <StepLabel
                    onClick={() => handleStepClick(index)}
                    sx={{
                      cursor: index === 0 && step === 2 ? "pointer" : "default",
                      "& .MuiStepLabel-label": {
                        fontSize: "0.875rem",
                        fontWeight: step - 1 === index ? 600 : 400,
                      }
                    }}
                  >
                    {stepData.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>

        {/* Step 1: Select Report */}
        <Collapse in={step === 1}>
          <div>
            <Typography variant="body2" className={classes.sectionTitle}>
              Choisissez le rapport que vous souhaitez générer :
            </Typography>

            <div className={classes.reportsGrid}>
              {availableReports.map((report) => (
                <Card
                  key={report.reportId}
                  onClick={() => handleReportSelect(report)}
                  className={classes.reportCard}
                >
                  <CardContent className={classes.reportCardContent}>
                    <Icon className={classes.reportIcon}>
                      description
                    </Icon>
                    <div style={{ flex: 1 }}>
                      <Typography variant="subtitle1" className={classes.reportTitle}>
                        {report.name || report.code}
                      </Typography>
                      {report.description && (
                        <Typography variant="body2" color="textSecondary" className={classes.reportDescription}>
                          {report.description}
                        </Typography>
                      )}
                    </div>
                    <Icon style={{ color: "#ccc", fontSize: "20px" }}>
                      chevron_right
                    </Icon>
                  </CardContent>
                </Card>
              ))}
            </div>

            {availableReports.length === 0 && (
              <div className={classes.emptyState}>
                <Icon className={classes.emptyStateIcon}>folder_off</Icon>
                <Typography variant="body2">Aucun rapport disponible</Typography>
              </div>
            )}
          </div>
        </Collapse>

        {/* Step 2: Select Format */}
        {step === 2 && (
          <div>
            <div className={classes.selectedReportBox}>
              <Typography variant="body2" className={classes.selectedReportLabel}>
                Rapport sélectionné :
              </Typography>
              <Typography variant="subtitle1" className={classes.selectedReportName}>
                {selectedReport?.name || selectedReport?.code}
              </Typography>
            </div>

            <Typography variant="body2" className={classes.sectionTitle}>
              Sélectionnez le format d'export souhaité :
            </Typography>

            <ToggleButtonGroup
              value={exportFormat}
              exclusive
              onChange={handleFormatChange}
              className={classes.formatSelector}
            >
              <ToggleButton value="xlsx" className={classes.formatButton}>
                <div className={classes.formatButtonContent}>
                  <Icon>table_chart</Icon>
                  <div className={classes.formatButtonText}>
                    <div className={classes.formatTitle}>XLSX</div>
                    <div className={classes.formatSubtitle}>Excel 2007+</div>
                  </div>
                </div>
              </ToggleButton>
              <ToggleButton value="xls" className={classes.formatButton}>
                <div className={classes.formatButtonContent}>
                  <Icon>table_chart</Icon>
                  <div className={classes.formatButtonText}>
                    <div className={classes.formatTitle}>XLS</div>
                    <div className={classes.formatSubtitle}>Excel 97-2003</div>
                  </div>
                </div>
              </ToggleButton>
              <ToggleButton value="csv" className={classes.formatButton}>
                <div className={classes.formatButtonContent}>
                  <Icon>insert_drive_file</Icon>
                  <div className={classes.formatButtonText}>
                    <div className={classes.formatTitle}>CSV</div>
                    <div className={classes.formatSubtitle}>Texte séparé</div>
                  </div>
                </div>
              </ToggleButton>
            </ToggleButtonGroup>

            <EAButton
              className={classes.submitButton}
              onClick={handleGenerate}
              content="Lancer la génération"
              icon="play_arrow"
            />
          </div>
        )}
        </div>
      </Card>
    </div>
  );
}

export default ReportGenerationFlow;
